import * as React from "react";
import lodash from "lodash";
import { toTruthy } from "../utils/array";

export type UseSearchableT<V> = {
  results: V[];
  query: string;
  setQuery: (query: string) => void;
};

function useSearchable<V extends Record<string, unknown>>(
  objects: V[],
  fields: string[],
): UseSearchableT<V> {
  const [searchable, setSearchable] = React.useState<string[]>([]);
  const [query, setQuery] = React.useState("");
  const [results, setResults] = React.useState(() => objects.concat([]));

  React.useEffect(() => {
    // eslint-disable-next-line no-console
    if (fields.length === 0) console.error("useSearchable() called without any fields to search.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, fields);

  // calculate searchable text when the list of objects to be searched or the fields we're searching under changes
  React.useEffect(() => {
    // map each object in the list of searchable objects to a single string containing all the relevant text we're searching from based on the fields
    const searchableList = objects.map((item) => {
      let text = "";
      for (const path of fields) {
        const value = lodash.get(item, path);
        if (typeof value === "string") text = `${text} ${value.trim().toLowerCase()}`;
      }
      return text;
    });
    setSearchable(searchableList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...fields, objects]);

  // calculate search results when the query changes or when the searchable text changes
  React.useEffect(() => {
    // tokenize the query string
    const tokens = query.trim().toLowerCase().split(" ").filter(toTruthy);

    if (tokens.length === 0) setResults([...objects]);
    else {
      const newResults: [number, V][] = [];
      let maxCount = 0;
      searchable.forEach((text, i) => {
        let count = 0;
        for (const token of tokens) if (text.includes(token)) count += 1;
        maxCount = Math.max(maxCount, count);
        if (count > 0) newResults.push([count, objects[+i]]);
      });
      // if we've found at least one match that has every token in the query, then we'll remove any less specific matches
      if (maxCount === tokens.length)
        setResults(
          newResults.reduce<V[]>((list, [count, item]) => {
            if (count === maxCount) list.push(item);
            return list;
          }, []),
        );
      // otherwise we return all possible matches sorted by relevance
      else setResults(newResults.sort((a, b) => b[0] - a[0]).map(([, item]) => item));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchable, query]);

  // return objects array when there's no query to prevent it from initially displaying no results due to delay from React's useEffect hook
  return { query, results: query ? results : objects, setQuery };
}

export default useSearchable;
